import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";

const SLIDES = [
  {
    tag: "Est. 1983 · Ghaziabad",
    title: "Advancing Excellence in Eye Care",
    highlight: "for Four Decades",
    subtitle: "A professional community of ophthalmologists dedicated to raising the standard of eye care through science, education and community outreach.",
    image: "/slider_2.jpg",
    cta1: { label: "Become a Member", to: "/become-member" },
    cta2: { label: "Upcoming Events", to: "/events" },
  },
  {
    tag: "Continuing Medical Education",
    title: "World-Class CME Programs",
    highlight: "& Fellowship Opportunities",
    subtitle: "Structured programs, workshops and hands-on training designed for practising ophthalmologists, residents and fellows.",
    image: "/slider_3.jpg",
    cta1: { label: "Explore CME Programs", to: "/cme-programs" },
    cta2: { label: "Announcements", to: "/announcements" },
  },
  {
    tag: "Community Outreach",
    title: "Preventing Avoidable Blindness",
    highlight: "Across Ghaziabad & NCR",
    subtitle: "Free screening camps, awareness drives and public education initiatives across the region — because vision changes lives.",
    image: "/slider_1.jpg",
    cta1: { label: "About the Society", to: "/about" },
    cta2: { label: "Contact Us", to: "/contact" },
  },
];

const AUTO_INTERVAL = 6500;

const normalizeSlide = (slide) => {
  const title = slide.title || "";
  const subtitle = slide.subtitle || slide.highlight || "";
  const description = slide.description || slide.subtitle || "";
  const primaryUrl = slide.buttonUrl || slide.cta1?.to || "/";
  const primaryLabel = slide.buttonText || slide.cta1?.label || "Learn More";

  return {
    ...slide,
    tag: slide.category || slide.tag || "General",
    title,
    highlight: subtitle,
    subtitle: description,
    image: slide.backgroundImage || slide.image,
    posterImage: slide.posterImage || "",
    cta1: { label: primaryLabel, to: primaryUrl },
    cta2: slide.cta2,
  };
};

const slideAssetUrl = (path) => {
  if (!path) return "";
  return path.startsWith("/uploads/") ? mediaUrl(path) : path;
};

export default function HeroSlider() {
  const [slides, setSlides] = React.useState(SLIDES);
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api.get("/slider")
      .then((r) => {
        const apiSlides = Array.isArray(r.data) ? r.data.filter((slide) => slide.backgroundImage || slide.image) : [];
        if (alive && apiSlides.length) {
          setSlides(apiSlides.map(normalizeSlide));
          setIdx(0);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const go = (n) => setIdx((idx + n + slides.length) % slides.length);

  React.useEffect(() => {
    if (paused) return;
    setProgress(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / AUTO_INTERVAL) * 100);
      setProgress(pct);
      if (elapsed >= AUTO_INTERVAL) setIdx((i) => (i + 1) % slides.length);
    }, 60);
    return () => clearInterval(tick);
  }, [idx, paused, slides.length]);

  const s = slides[idx] || normalizeSlide(SLIDES[0]);
  const hasPoster = Boolean(s.posterImage);

  return (
    <section className="relative overflow-hidden bg-brand-dark" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Slides */}
      <div className="relative h-[86vh] min-h-[620px] max-h-[820px]">
        <AnimatePresence mode="sync">
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img src={slideAssetUrl(s.image)} alt={s.title} className="w-full h-full object-cover kenburns" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/95 via-brand-dark/70 to-brand-dark/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 to-transparent" />
            <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="relative h-full container-wide flex items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className={`w-full text-white ${hasPoster ? "grid lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] items-center gap-10 xl:gap-14" : "max-w-3xl"}`}
            >
              <div className={hasPoster ? "max-w-3xl" : ""}>
                <div className="inline-flex items-center gap-2.5 rounded-full bg-white/10 border border-white/20 backdrop-glass px-4 py-2 text-[11px] uppercase tracking-[0.28em] font-semibold">
                  <Sparkles size={12} className="text-brand-accent" /> {s.tag}
                </div>
                <h1 className="font-display text-[clamp(2.5rem,6vw,4.75rem)] font-extrabold leading-[1.02] tracking-tight mt-6 text-balance">
                  {s.title}
                  {s.highlight && (
                    <>
                      <br />
                      <span className="bg-gradient-to-r from-brand-secondary via-white to-brand-secondary bg-clip-text text-transparent">{s.highlight}</span>
                    </>
                  )}
                </h1>
                {s.subtitle && (
                  <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl font-light">
                    {s.subtitle}
                  </p>
                )}
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to={s.cta1.to} className="btn-hero-light" data-testid="hero-primary-cta">
                    {s.cta1.label} <ArrowRight size={18} />
                  </Link>
                  {s.cta2 && (
                    <Link to={s.cta2.to} className="btn-hero-outline" data-testid="hero-secondary-cta">
                      {s.cta2.label}
                    </Link>
                  )}
                </div>
              </div>
              {hasPoster && (
                <motion.div
                  key={s.posterImage}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                  className="hidden md:flex justify-center lg:justify-end"
                >
                  <div className="group max-w-[420px] w-full rounded-[22px] border border-white/25 bg-white/12 shadow-[0_28px_70px_-32px_rgba(0,0,0,0.75)] backdrop-glass overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_34px_82px_-34px_rgba(0,0,0,0.85)]">
                    <img src={slideAssetUrl(s.posterImage)} alt={`${s.title} poster`} className="w-full max-h-[560px] object-contain bg-white/95" loading="eager" />
                  </div>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Arrows */}
        <button onClick={() => go(-1)} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-glass text-white border border-white/25 flex items-center justify-center transition-all hover:scale-110" aria-label="Previous" data-testid="hero-prev">
          <ChevronLeft size={22} />
        </button>
        <button onClick={() => go(1)} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-glass text-white border border-white/25 flex items-center justify-center transition-all hover:scale-110" aria-label="Next" data-testid="hero-next">
          <ChevronRight size={22} />
        </button>

        {/* Progress + counters */}
        <div className="absolute bottom-8 left-0 right-0 z-10">
          <div className="container-wide flex items-center gap-6">
            <div className="text-white font-display text-2xl font-bold tracking-tight">
              <span className="text-white">{String(idx + 1).padStart(2, "0")}</span>
              <span className="text-white/40"> / {String(slides.length).padStart(2, "0")}</span>
            </div>
            <div className="flex-1 h-[2px] bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-brand-secondary to-brand-accent transition-[width] duration-100 ease-linear" style={{ width: `${progress}%` }} />
            </div>
            <div className="hidden sm:flex items-center gap-2">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/70"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
